import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Container, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import { fCurrency } from '../../../../utils/formatNumber';
import { getCashFlowSubCategories, getGrnPaymentList, handleGrnPaymentApproval } from '../../../../redux/slices/financeRedux/cashFlowRedux';
import { Block, CheckCircleOutline, Edit, PictureAsPdf, PlayCircleFilled } from '@material-ui/icons';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../config';
import { accessVerify, capitalize, handleNumber, removeUnwantedStockBarCodeList, safeJSONParse, utcMoment } from '../../../../utils/common';
import Iconify from '../../../../components/Iconify';
import GrnPaymentDrawer from './grnPayment/grnPaymentDrawer';
import { getPurchaseNotes, uploadPurchaseNotesDocuments } from '../../../../redux/slices/purchaseRedux/purchaseNotesRedux';
import { BANK_PAYMENT_TYPES, CASHFLOW_PAYMENT_STATUS } from '../../../../utils/constants';
import PdfViewerDrawer from '../../../../components/pdfViewerDrawer';
import { toast } from 'react-toastify';
import CashFlowImageUploader, { isImageDocument, isPdfFile } from '../cashflow/components/cashflowImageUploader';
import { fileUpload, imageUpload } from '../../../../inteceptor';
import ImageViewer from 'react-simple-image-viewer';

export default function GrnPayments({ statusValue = 0, statusName = '', viewPermission = '' }) {
  const dataModel = {
    id: null,
    dateTime: null,
    paymentDate: null,
    scheduledDate: null,
    paidDate: null,
    supplierOrderId: null,
    cashbookLedgerId: null,
    bankPaymentType: null,
    referenceNote: null,
    amount: 0,
    locationId: null,
    status: 2,
    addedBy: null,
    approvedBy: null,
    paymentMarkedBy: null,
    declineReason: null,
    declineReferenceNote: null,
    declineRequestedBy: null,
    declineRequestedAt: null,
    declineApprovedBy: null,
    declineApprovedAt: null,
    declineRejectedBy: null,
    declineRejectedAt: null,
    linkedDocuments: [],
  };
  const { themeStretch } = useSettings();
  const [rowSelection, setRowSelection] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  const [grnPaymentList, setGrnPaymentList] = useState([]);
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const [selectedRowForMarkPayment, setSelectedRowForMarkPayment] = useState(null);
  const [paidProofUrls, setPaidProofUrls] = useState([]);
  const [paidProofFiles, setPaidProofFiles] = useState([]);
  const [isPaidProofUploading, setIsPaidProofUploading] = useState(false);
  const [isPaidProofViewerOpen, setIsPaidProofViewerOpen] = useState(false);
  const [currentPaidProofImage, setCurrentPaidProofImage] = useState(0);
  const { selectedLocation } = useSelector((state) => state?.location);
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [paymentVoucher, setPaymentVoucher] = useState({});
  const [workflowLog, setWorkflowLog] = useState(null);
  const [selectedRowForDecline, setSelectedRowForDecline] = useState(null);
  const [isDeclineDrawerOpen, setIsDeclineDrawerOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineReferenceNote, setDeclineReferenceNote] = useState('');
  const [declineProofUrls, setDeclineProofUrls] = useState([]);
  const [declineFiles, setDeclineFiles] = useState([]);
  const [isDeclineUploading, setIsDeclineUploading] = useState(false);
  const [isDeclineViewerOpen, setIsDeclineViewerOpen] = useState(false);
  const [currentDeclineImage, setCurrentDeclineImage] = useState(0);
  const [declineRequestDetailRow, setDeclineRequestDetailRow] = useState(null);
  const declineImageGallery = declineProofUrls?.filter(isImageDocument) || [];
  const paidProofImageGallery = paidProofUrls?.filter(isImageDocument) || [];

  let tempGrnPaymentList;
  let addDataStatus;
  ({ grnPaymentList: tempGrnPaymentList, addData: addDataStatus } = useSelector((state) => state?.cashFlow));
  const { loggedInUser } = useSelector((state) => state?.user);

  let purchaseNoteList;
  ({ data: purchaseNoteList } = useSelector((state) => state?.purchaseNotes));
  const isDeclineRequestView = !Array.isArray(statusValue) && Number(statusValue) === 6;

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getGrnPaymentList(selectedLocation?.id));
      dispatch(getPurchaseNotes());
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    // dispatch(getPurchaseNotes());
    dispatch(getCashFlowSubCategories());
  }, []);

  useEffect(() => {
    if (addDataStatus?.time) {
      dispatch(getGrnPaymentList(selectedLocation?.id));
      dispatch(getPurchaseNotes());
    }
  }, [addDataStatus]);

  useEffect(() => {
    const statusValueList = Array.isArray(statusValue) ? statusValue : [statusValue];
    setGrnPaymentList(
      (tempGrnPaymentList || [])
        ?.filter((value) => statusValueList.includes(Number(value?.status)))
        .map((value) => {
          return {
            ...value,
            declineSource: Number(value?.status) === 4 ? 'Before Confirmation' : Number(value?.status) === 7 ? 'After Confirmation' : undefined,
          };
        })
    );
    setIsModelOpen(false);
  }, [tempGrnPaymentList, statusValue]);

  const totalAmount = useMemo(
    () =>
      grnPaymentList.reduce((total, value) => {
        return total + handleNumber(value?.amount);
      }, 0),
    [grnPaymentList]
  );

  const normalizeDocumentUrls = (linkedDocuments) => {
    const parsedDocuments = Array.isArray(linkedDocuments) ? linkedDocuments : safeJSONParse(linkedDocuments);
    if (!Array.isArray(parsedDocuments)) return [];
    return parsedDocuments
      .map((value) => {
        if (typeof value === 'string') return value;
        return value?.url;
      })
      .filter((value) => value);
  };

  const getItemsForInvoice = (rowData) => {
    if (rowData?.supplierorder_foodsupplierpricing) {
      const parsedRowData = safeJSONParse(rowData?.supplierorder_foodsupplierpricing);
      return parsedRowData?.map((value, index) => {
        const jsonData = safeJSONParse(value?.jsonData);
        const pricePerSupplierUnit = (jsonData?.price / jsonData?.purchaseUnitQty) * jsonData?.storeUnitQty;
        return {
          id: index,
          title: jsonData?.FoodName,
          foodId: value?.foodId,
          description: `Final Price is checked on ${jsonData?.formatted_dateTime}.`,
          quantity: Number(value?.receivedQty) / (Number(value?.storeQty) / Number(value?.supplierQty)),
          storeUnitQty: jsonData?.storeUnitQty + ' ' + jsonData?.unitSymbol,
          price: pricePerSupplierUnit,
        };
      });
    }
  };

  const columns = !grnPaymentList?.[0]
    ? []
    : Object.keys(grnPaymentList?.[0])
        .map((value) => {
          if (
            [
              'addedBy',
              'approvedBy',
              'paymentMarkedBy',
              'declineRequestedBy',
              'declineApprovedBy',
              'declineRejectedBy',
              'locationAddress',
              'locationContactNumber',
              'grn_payment_workflow_logs',
            ].includes(value)
          ) {
            return null;
          } else if (value === 'linkedDocuments') {
            return null;
          } else if (value === 'datetime') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return row.original?.[value] ? utcMoment(row.original?.[value]).format('YYYY-MM-DD').toString() : '-';
              },
            };
          } else if (value === 'paidDate') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return row.original?.[value] ? utcMoment(row.original?.[value]).format('YYYY-MM-DD').toString() : '-';
              },
            };
          } else if (['declineRequestedAt', 'declineApprovedAt', 'declineRejectedAt'].includes(value)) {
            return {
              accessorKey: value,
              header:
                value === 'declineRequestedAt'
                  ? 'Decline Requested At'
                  : value === 'declineApprovedAt'
                  ? 'Decline Approved At'
                  : 'Decline Rejected At',
              Cell: ({ row }) => (row.original?.[value] ? utcMoment(row.original?.[value]).format('YYYY-MM-DD HH:mm:ss').toString() : '-'),
            };
          } else if (value === 'supplierOrderId') {
            return {
              accessorKey: value,
              header: 'GRN Number',
              Cell: ({ cell, row }) => {
                return row.original?.[value];
              },
            };
          } else if (value === 'supplierInvoiceNumber') {
            return {
              accessorKey: value,
              header: 'Supplier Invoice Number',
              Cell: ({ row }) => row?.original?.supplierInvoiceNumber || '-',
            };
          } else if (value === 'grnDate') {
            return {
              accessorKey: value,
              header: 'GRN Date',
              Cell: ({ row }) => row?.original?.grnDate || '-',
            };
          } else if (value === 'grnTime') {
            return {
              accessorKey: value,
              header: 'GRN Time',
              Cell: ({ row }) => row?.original?.grnTime || '-',
            };
          } else if (value === 'paymentDate') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return row.original?.[value] ? utcMoment(row.original?.[value]).format('YYYY-MM-DD').toString() : '-';
              },
            };
          } else if (value === 'scheduledDate') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return row.original?.[value] ? utcMoment(row.original?.[value]).format('YYYY-MM-DD').toString() : '-';
              },
            };
          } else if (value === 'bankPaymentType') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return BANK_PAYMENT_TYPES?.find((val) => val.id === Number(row.original[value]))?.label || '-';
              },
            };
          } else if (value === 'amount') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return fCurrency(row.original[value]);
              },
            };
          } else if (value === 'addedUserName') {
            return {
              accessorKey: value,
              header: 'Initiated By',
            };
          } else if (value === 'declineRequestedByName') {
            return {
              accessorKey: value,
              header: 'Decline Requested By',
              Cell: ({ row }) => row?.original?.declineRequestedByName || '-',
            };
          } else if (value === 'declineApprovedByName') {
            return {
              accessorKey: value,
              header: 'Decline Approved By',
              Cell: ({ row }) => row?.original?.declineApprovedByName || '-',
            };
          } else if (value === 'declineRejectedByName') {
            return {
              accessorKey: value,
              header: 'Decline Rejected By',
              Cell: ({ row }) => row?.original?.declineRejectedByName || '-',
            };
          } else if (value === 'declineReason') {
            return {
              accessorKey: value,
              header: 'Decline Reason',
              Cell: ({ row }) => row?.original?.declineReason || '-',
            };
          } else if (value === 'declineReferenceNote') {
            return {
              accessorKey: value,
              header: 'Decline Reference',
              Cell: ({ row }) => row?.original?.declineReferenceNote || '-',
            };
          } else if (value === 'declineSource') {
            return {
              accessorKey: value,
              header: 'Decline Source',
              Cell: ({ row }) => row?.original?.declineSource || '-',
            };
          } else if (value === 'status') {
            return {
              accessorKey: value,
              header: capitalize(value),
              Cell: ({ cell, row }) => {
                return CASHFLOW_PAYMENT_STATUS?.find((value) => value.id === Number(row.original?.status))?.name;
              },
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        .filter((value) => value);

  const parseWorkflowLogs = (rowData) => {
    const rawLog = rowData?.workflowLog;
    if (!rawLog) return [];

    const parsedLog = safeJSONParse(rawLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const parsePaymentVoucherWorkflowLogs = (rowData) => {
    const rawLog = rowData?.grn_payment_workflow_logs;
    if (!rawLog) return [];

    const parsedLog = safeJSONParse(rawLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const handleDownload = async (row) => {
    const flowStatus = CASHFLOW_PAYMENT_STATUS.find((value) => value?.id === Number(row.original?.status));
    const selectedPurchaseNote = purchaseNoteList?.find((value) => value?.id === row?.original?.supplierOrderId);
    const urlList = safeJSONParse(selectedPurchaseNote?.linkedDocuments)?.map((value) => value?.url);

    const companyDetails = row?.original?.locationId
      ? {
          name: COMPANY_DATA_FOR_PDF?.name,
          address: row?.original?.locationAddress,
          phone: row?.original?.locationContactNumber,
        }
      : {
          name: COMPANY_DATA_FOR_PDF?.name,
          address: selectedLocation?.address,
          phone: selectedLocation?.contactNumber,
        };
    const supplierDetails = {
      name: selectedPurchaseNote?.supplierName,
      address: selectedPurchaseNote?.supplierAddress,
      phone: selectedPurchaseNote?.supplierCompanyPhone,
    };

    setPdfRow({
      invoiceNumber: `GRN-${selectedPurchaseNote?.id}`,
      createDate: utcMoment(selectedPurchaseNote?.date).format('DD MMMM yyyy').toString(),
      dueDate: utcMoment(selectedPurchaseNote?.date)
        .add(selectedPurchaseNote?.supplierNumberOfDaysForPayment || 30, 'days')
        .format('DD MMMM yyyy')
        .toString(),
      status: flowStatus?.name,
      discount: 0,
      taxes: 0,
      paidAmount: handleNumber(selectedPurchaseNote?.paidamount),
      totalPrice: handleNumber(selectedPurchaseNote?.paymentforreceivings) - handleNumber(selectedPurchaseNote?.paidamount),
      subTotalPrice: handleNumber(selectedPurchaseNote?.paymentforreceivings),
      topic: 'Item Details',
      invoiceFrom: supplierDetails,
      invoiceTo: companyDetails,
      items: getItemsForInvoice(selectedPurchaseNote),
      stockCodes: removeUnwantedStockBarCodeList(safeJSONParse(selectedPurchaseNote?.stockCodes)) || [],
      linkedDocuments: urlList?.map((value) => value?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)) || [],
      supplierInvoiceNumber: selectedPurchaseNote.supplierInvoiceNumber,
    });
    setPaymentVoucher({
      voucherId: `Voucher_${row.original?.id}`,
      voucherStatus: CASHFLOW_PAYMENT_STATUS?.find((value) => value.id === Number(row.original?.status))?.name,
      voucherCreatedDate: utcMoment(row.original?.datetime).format('DD MMMM yyyy').toString(),
      voucherScheduledDate: utcMoment(row?.original?.scheduledDate).format('DD MMMM yyyy').toString(),
      voucherPaymentMethod: `${row?.original?.cashBookLedger} - ${BANK_PAYMENT_TYPES?.find((val) => val.id === Number(row.original?.bankPaymentType))?.label}`,
      voucherReferenceNumber: row?.original?.referenceNote || '-',
      voucherInitiatedByUser: row?.original?.addedUserName || '-',
      voucherApprovedByUser: row?.original?.approvedByName || '-',
      voucherMarkedBy: row?.original?.paymentMarkedByName || '-',
      paymentFrom: companyDetails,
      paymentTo: supplierDetails,
      workflowLogs: parsePaymentVoucherWorkflowLogs(row?.original),
    });
    setWorkflowLog(parseWorkflowLogs(selectedPurchaseNote));
    setIsPdfDrawerOpen(true);
  };

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');

  const openDrawer = (row) => {
    setSelectedRowForMarkPayment(row);
    setPaymentDate(row?.original?.paidDate ? utcMoment(row.original.paidDate).format('YYYY-MM-DD') : utcMoment().format('YYYY-MM-DD'));
    setPaidProofUrls([]);
    setPaidProofFiles([]);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRowForMarkPayment(null);
    setPaidProofUrls([]);
    setPaidProofFiles([]);
    setPaymentDate('');
  };
  const handleDateChange = (event) => setPaymentDate(event.target.value);

  const handleSubmitPayment = (row) => {
    if (!paymentDate) {
      toast.error('Paid date is required.');
      return;
    }
    if (!paidProofUrls.length) {
      toast.error('At least one payment proof document is required.');
      return;
    }

    dispatch(handleGrnPaymentApproval({ id: row?.original?.id, status: 5, paidDate: paymentDate, paidProofUrls }));
    closeDrawer();
  };

  const uploadPaidProofFile = (file) => {
    return new Promise((resolve, reject) => {
      const uploadMethod = isPdfFile(file) ? fileUpload : imageUpload;
      uploadMethod(
        file,
        `Finance/GRNPaymentProof/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => reject(new Error(errorMessage))
      );
    });
  };

  const handlePaidProofUpload = async () => {
    if (!paidProofFiles.length) {
      toast.warning('Please select at least one payment proof document to upload.');
      return;
    }

    setIsPaidProofUploading(true);
    for (const file of paidProofFiles) {
      try {
        setPaidProofFiles((value) => value.map((fileValue) => (file?.id === fileValue?.id ? { ...fileValue, isUploading: true } : fileValue)));
        const data = await uploadPaidProofFile(file);
        setPaidProofFiles((value) => value.filter((fileValue) => file?.id !== fileValue?.id));
        if (data?.status) {
          setPaidProofUrls((value) => [...value, data?.url]);
        }
      } catch (error) {
        toast.error(error?.message || 'Payment proof upload failed.');
        setPaidProofFiles((value) => value.map((fileValue) => (file?.id === fileValue?.id ? { ...fileValue, isUploading: false } : fileValue)));
      }
    }
    setIsPaidProofUploading(false);
  };

  const openPaidProofViewer = (index) => {
    setCurrentPaidProofImage(index);
    setIsPaidProofViewerOpen(true);
  };

  const closePaidProofViewer = () => {
    setCurrentPaidProofImage(0);
    setIsPaidProofViewerOpen(false);
  };

  const openDeclineDrawer = (row) => {
    setSelectedRowForDecline(row);
    setDeclineReason('');
    setDeclineReferenceNote('');
    setDeclineProofUrls([]);
    setDeclineFiles([]);
    setIsDeclineDrawerOpen(true);
  };

  const closeDeclineDrawer = () => {
    setIsDeclineDrawerOpen(false);
    setSelectedRowForDecline(null);
    setDeclineProofUrls([]);
    setDeclineFiles([]);
    setDeclineReason('');
    setDeclineReferenceNote('');
  };

  const uploadDeclineFile = (file) => {
    return new Promise((resolve, reject) => {
      const uploadMethod = isPdfFile(file) ? fileUpload : imageUpload;
      uploadMethod(
        file,
        `Finance/PurchaseNote_Invoice/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleDeclineImageUpload = async () => {
    if (!selectedRowForDecline?.original?.supplierOrderId) {
      toast.error('Please select a GRN payment first.');
      return;
    }
    if (!declineFiles.length) {
      toast.warning('Please select at least one document to upload.');
      return;
    }

    setIsDeclineUploading(true);
    for (const file of declineFiles) {
      try {
        setDeclineFiles((value) =>
          value.map((fileValue) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: true,
              };
            }
            return fileValue;
          })
        );

        const data = await uploadDeclineFile(file);
        setDeclineFiles((value) => value.filter((fileValue) => file?.id !== fileValue?.id));

        if (data?.status) {
          setDeclineProofUrls((prevState) => [...prevState, data?.url]);
          await dispatch(uploadPurchaseNotesDocuments({ supplierorderId: selectedRowForDecline?.original?.supplierOrderId, url: data?.url }));
        }
      } catch (error) {
        toast.error(error?.message || 'Document upload failed.');
        setDeclineFiles((value) =>
          value.map((fileValue) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: false,
              };
            }
            return fileValue;
          })
        );
      }
    }
    setIsDeclineUploading(false);
  };

  const submitDeclineRequest = () => {
    const reason = declineReason.trim();
    if (!reason) {
      toast.error('Decline reason is required.');
      return;
    }
    if (!declineProofUrls.length) {
      toast.error('At least one proof document is required.');
      return;
    }

    dispatch(
      handleGrnPaymentApproval({
        id: selectedRowForDecline?.original?.id,
        status: 6,
        declineReason: reason,
        declineReferenceNote: declineReferenceNote.trim() || null,
        declineProofUrls,
      })
    );
    closeDeclineDrawer();
  };

  const openDeclineImageViewer = (index) => {
    setCurrentDeclineImage(index);
    setIsDeclineViewerOpen(true);
  };

  const closeDeclineImageViewer = () => {
    setCurrentDeclineImage(0);
    setIsDeclineViewerOpen(false);
  };

  const openDeclineRequestDetails = (row) => {
    if (isDeclineRequestView) {
      setDeclineRequestDetailRow(row?.original || null);
    }
  };

  const closeDeclineRequestDetails = () => {
    setDeclineRequestDetailRow(null);
  };

  const getDeclineRequestDetailRows = (rowData) => [
    ['Voucher ID', rowData?.id],
    ['GRN Number', rowData?.supplierOrderId],
    ['Supplier', rowData?.supplierName],
    ['Supplier Invoice Number', rowData?.supplierInvoiceNumber],
    ['GRN Date', rowData?.grnDate],
    ['GRN Time', rowData?.grnTime],
    ['Location', rowData?.locationName],
    ['Cashbook Ledger', rowData?.cashBookLedger],
    ['Bank Payment Type', BANK_PAYMENT_TYPES?.find((val) => val.id === Number(rowData?.bankPaymentType))?.label || '-'],
    ['Payment Reference', rowData?.referenceNote],
    ['Amount', fCurrency(rowData?.amount)],
    ['Payment Date', rowData?.paymentDate ? utcMoment(rowData?.paymentDate).format('YYYY-MM-DD').toString() : '-'],
    ['Scheduled Date', rowData?.scheduledDate ? utcMoment(rowData?.scheduledDate).format('YYYY-MM-DD').toString() : '-'],
    ['Initiated By', rowData?.addedUserName],
    ['Confirmed By', rowData?.approvedByName],
    ['Decline Reason', rowData?.declineReason],
    ['Decline Reference', rowData?.declineReferenceNote],
    ['Decline Requested By', rowData?.declineRequestedByName],
    ['Decline Requested At', rowData?.declineRequestedAt ? utcMoment(rowData?.declineRequestedAt).format('YYYY-MM-DD HH:mm:ss').toString() : '-'],
  ];

  return (
    <Page title={`GRN Payment (${statusName})`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`GRN Payment (${statusName})`}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Finance', href: PATH_DASHBOARD.purchasing.root }, { name: `GRN Payment (${statusName})` }]}
          action={
            statusValue === 2 && accessVerify(`FINANCE_GRN_${viewPermission}_ADD`) ? (
              <Button
                variant='contained'
                startIcon={<Iconify icon='eva:plus-fill' />}
                onClick={() => {
                  setIsModelOpen(true);
                  setIsAdd(true);
                  setSelectedDataObj(dataModel);
                }}
              >
                Initiate GRN Payment
              </Button>
            ) : (
              <></>
            )
          }
        />

        {accessVerify(`FINANCE_GRN_${viewPermission}_VIEW`) && (
          <>
            <Card sx={{ m: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: '400' }}>
                    Total Amount
                  </Typography>
                  <Typography variant='h6' sx={{ color: 'red', fontWeight: '500' }}>
                    {fCurrency(totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </Card>
            <DataGridTable
              name={'GrnPayment'}
              data={grnPaymentList}
              column={columns}
              isLoading={false}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              enableRowActions={true}
              rowActionsColumnSize={statusValue === 3 ? 360 : 220}
              isRowClickable={isDeclineRequestView}
              onRowClick={openDeclineRequestDetails}
              renderRowActionItems={(row) => [
                <>
                  {[1, 2].includes(row?.original?.status) && (
                    <>
                      {accessVerify(`FINANCE_GRN_${viewPermission}_UPDATE`) && (
                        <Tooltip title='Confirm Payment'>
                          <IconButton
                            color='success'
                            size='small'
                            onClick={() => {
                              dispatch(handleGrnPaymentApproval({ ...row?.original, status: 3 }));
                            }}
                          >
                            <CheckCircleOutline />
                          </IconButton>
                        </Tooltip>
                      )}
                      {accessVerify(`FINANCE_GRN_${viewPermission}_UPDATE`) && (
                        <Tooltip title='Decline Payment'>
                          <IconButton
                            color='error'
                            size='small'
                            onClick={() => {
                              dispatch(handleGrnPaymentApproval({ ...row?.original, status: 4 }));
                            }}
                          >
                            <Block />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  {[3].includes(row?.original?.status) && accessVerify(`FINANCE_GRN_${viewPermission}_UPDATE`) && (
                    <>
                      <Tooltip title='Mark Payment'>
                        <Button
                          color='success'
                          onClick={() => {
                            openDrawer(row);
                          }}
                          size='small'
                          startIcon={<CheckCircleOutline fontSize='small' />}
                          sx={{
                            padding: '4px 8px',
                            minWidth: 0,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                          }}
                        >
                          Paid
                        </Button>
                      </Tooltip>
                      <Tooltip title='Request Decline'>
                        <Button
                          color='error'
                          onClick={() => {
                            openDeclineDrawer(row);
                          }}
                          size='small'
                          startIcon={<Block fontSize='small' />}
                          sx={{
                            padding: '4px 8px',
                            minWidth: 0,
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            ml: 1,
                          }}
                        >
                          Request Decline
                        </Button>
                      </Tooltip>
                    </>
                  )}

                  {[6].includes(row?.original?.status) && accessVerify(`FINANCE_GRN_${viewPermission}_UPDATE`) && (
                    <>
                      <Tooltip title='Approve Decline'>
                        <IconButton
                          color='success'
                          size='small'
                          onClick={(event) => {
                            event.stopPropagation();
                            dispatch(handleGrnPaymentApproval({ id: row?.original?.id, status: 7 }));
                          }}
                        >
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Reject Decline Request'>
                        <IconButton
                          color='error'
                          size='small'
                          onClick={(event) => {
                            event.stopPropagation();
                            dispatch(handleGrnPaymentApproval({ id: row?.original?.id, status: 3 }));
                          }}
                        >
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {!isDeclineRequestView && Number(row?.original?.status) !== 3 && accessVerify(`FINANCE_GRN_${viewPermission}_UPDATE`) && (
                    <Tooltip title='Edit Record'>
                      <IconButton
                        color='warning'
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsAdd(false);
                          setSelectedDataObj({
                            ...row?.original,
                            linkedDocuments: safeJSONParse(row?.original?.linkedDocuments),
                          });
                          setIsModelOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}

                  {row?.original?.status === 'PROCESSING' ? (
                    <Tooltip title='Verify receivings'>
                      <IconButton
                        color='success'
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsModelOpen(true);
                          setIsAdd(false);
                        }}
                      >
                        <PlayCircleFilled />
                      </IconButton>
                    </Tooltip>
                  ) : (
                  <Tooltip title='Download PDF'>
                      <IconButton
                        color='error'
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDownload(row);
                        }}
                      >
                        <PictureAsPdf />
                      </IconButton>
                    </Tooltip>
                  )}
                </>,
              ]}
            />

            <Drawer anchor='right' open={isDrawerOpen} onClose={closeDrawer}>
              <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }}>
                <Typography variant='h6' sx={{ mb: 2 }}>
                  Mark Payment as Paid
                </Typography>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  GRN Number: {selectedRowForMarkPayment?.original?.supplierOrderId || '-'}
                </Typography>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Supplier Invoice Number: {selectedRowForMarkPayment?.original?.supplierInvoiceNumber || '-'}
                </Typography>
                <TextField
                  label='Paid Date'
                  type='date'
                  fullWidth
                  required
                  value={paymentDate}
                  onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }}
                  margin='normal'
                />
                <Box sx={{ my: 2 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Payment Proof
                  </Typography>
                  <CashFlowImageUploader
                    files={paidProofFiles}
                    setFiles={setPaidProofFiles}
                    handleImageUpload={handlePaidProofUpload}
                    documentGallery={paidProofUrls}
                    openImageViewer={openPaidProofViewer}
                    isUploading={isPaidProofUploading}
                    allowPdf
                  />
                  {isPaidProofViewerOpen && (
                    <ImageViewer
                      src={paidProofImageGallery}
                      currentIndex={currentPaidProofImage}
                      disableScroll={false}
                      closeOnClickOutside={true}
                      onClose={closePaidProofViewer}
                    />
                  )}
                </Box>
                <Button
                  variant='contained'
                  color='success'
                  onClick={() => handleSubmitPayment(selectedRowForMarkPayment)}
                  fullWidth
                  disabled={isPaidProofUploading}
                >
                  Confirm Payment
                </Button>
              </Box>
            </Drawer>
          </>
        )}

        <Drawer anchor='right' open={isDeclineDrawerOpen} onClose={closeDeclineDrawer}>
          <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Request Decline
            </Typography>
            <Typography variant='body2' sx={{ mb: 2 }}>
              GRN Number: {selectedRowForDecline?.original?.supplierOrderId || '-'}
            </Typography>
            <TextField
              label='Decline Reason'
              multiline
              minRows={3}
              fullWidth
              required
              value={declineReason}
              onChange={(event) => setDeclineReason(event.target.value)}
              margin='normal'
            />
            <TextField
              label='Reference Number'
              fullWidth
              value={declineReferenceNote}
              onChange={(event) => setDeclineReferenceNote(event.target.value)}
              margin='normal'
            />
            <Box sx={{ my: 2 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Proof Documents
              </Typography>
              <CashFlowImageUploader
                files={declineFiles}
                setFiles={setDeclineFiles}
                handleImageUpload={handleDeclineImageUpload}
                documentGallery={declineProofUrls}
                openImageViewer={openDeclineImageViewer}
                isUploading={isDeclineUploading}
                allowPdf
              />
              {isDeclineViewerOpen && (
                <ImageViewer
                  src={declineImageGallery}
                  currentIndex={currentDeclineImage}
                  disableScroll={false}
                  closeOnClickOutside={true}
                  onClose={closeDeclineImageViewer}
                />
              )}
            </Box>
            <Button variant='contained' color='error' onClick={submitDeclineRequest} fullWidth disabled={isDeclineUploading}>
              Submit Decline Request
            </Button>
          </Box>
        </Drawer>

        <Dialog open={Boolean(declineRequestDetailRow)} onClose={closeDeclineRequestDetails} maxWidth='md' fullWidth>
          <DialogTitle>Decline Request Details</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: 1.25 }}>
              {getDeclineRequestDetailRows(declineRequestDetailRow).map(([label, value]) => (
                <React.Fragment key={label}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {label}
                  </Typography>
                  <Typography variant='body2'>{value || '-'}</Typography>
                </React.Fragment>
              ))}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Proof Documents
              </Typography>
              {normalizeDocumentUrls(declineRequestDetailRow?.linkedDocuments).length ? (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {normalizeDocumentUrls(declineRequestDetailRow?.linkedDocuments).map((url, index) => (
                    <Button key={url} size='small' variant='outlined' onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                      Proof {index + 1}
                    </Button>
                  ))}
                </Box>
              ) : (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  No proof documents found.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 3 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Workflow Logs
              </Typography>
              {parsePaymentVoucherWorkflowLogs(declineRequestDetailRow).length ? (
                <Box sx={{ display: 'grid', gap: 1 }}>
                  {parsePaymentVoucherWorkflowLogs(declineRequestDetailRow).map((log, index) => (
                    <Box key={`${log?.id || index}-${log?.action || ''}`} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Typography variant='body2' sx={{ fontWeight: 500 }}>
                        {log?.label || log?.action || '-'}
                      </Typography>
                      <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block' }}>
                        {log?.user || '-'} | {log?.date || '-'}
                      </Typography>
                      {log?.remarks && <Typography variant='body2'>{log.remarks}</Typography>}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  No workflow logs found.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeclineRequestDetails}>Close</Button>
          </DialogActions>
        </Dialog>

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} paymentVoucher={paymentVoucher} invoice={pdfRow} workflowLog={workflowLog} />
        <GrnPaymentDrawer isModelOpen={isModelOpen} setIsModelOpen={setIsModelOpen} selectedDataObj={selectedDataObj} setSelectedDataObj={setSelectedDataObj} isAdd={isAdd} />
      </Container>
    </Page>
  );
}
